/**
 * Phase 6c — Executive Threshold Change (Approval Center Integration)
 * Permission: EXEC_THRESHOLD_REQUEST (create/list), EXEC_THRESHOLD_APPROVE (approve/reject)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Scale, Check, X } from "lucide-react";

const APPROVE_ROLES = new Set(["super_admin", "direktur_utama"]);
const REQUEST_ROLES = new Set(["super_admin", "direktur_utama", "direktur_operasional"]);

const STATUS_BADGE = {
  PENDING:  "bg-amber-100 text-amber-900 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-200",
  REJECTED: "bg-rose-100 text-rose-900 border-rose-200",
};

export default function ExecutiveThresholdChanges() {
  const { user } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ alert_code: "", proposed_warning: "", proposed_critical: "", reason: "" });

  const canRequest = REQUEST_ROLES.has(user?.role);
  const canApprove = APPROVE_ROLES.has(user?.role);

  const load = () => {
    setErr(null);
    api.get("/executive/alert-config")
      .then(({ data }) => setConfigs(data.items || []))
      .catch(() => {});
    api.get("/executive/threshold-changes")
      .then(({ data }) => setRows(data.items || []))
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses permintaan perubahan threshold.");
        else setErr(formatApiError(e));
      });
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.alert_code || !form.reason || form.reason.length < 5) {
      toast.error("Pilih alert code dan isi alasan minimal 5 karakter.");
      return;
    }
    try {
      await api.post("/executive/threshold-changes", {
        alert_code: form.alert_code,
        proposed_warning: form.proposed_warning === "" ? null : Number(form.proposed_warning),
        proposed_critical: form.proposed_critical === "" ? null : Number(form.proposed_critical),
        reason: form.reason,
      });
      toast.success("Permintaan perubahan threshold diajukan.");
      setForm({ alert_code: "", proposed_warning: "", proposed_critical: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const act = async (id, action) => {
    const note = window.prompt(`Catatan ${action === "approve" ? "persetujuan" : "penolakan"} (opsional):`) || "";
    try {
      await api.post(`/executive/threshold-changes/${id}/${action}`, { decision_note: note });
      toast.success(`Permintaan ${action === "approve" ? "disetujui" : "ditolak"}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-threshold-changes-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-threshold-no-access">
          {err}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exec-threshold-changes-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Scale className="h-6 w-6 text-sky-700" /> Permintaan Perubahan Threshold
        </h1>
        <p className="text-xs md:text-sm text-slate-500">
          Threshold alert tidak diubah otomatis. Setiap perubahan wajib melalui approval oleh super admin atau direktur utama.
          Setelah disetujui, perubahan tercatat di audit trail eksekutif.
        </p>
      </div>

      {/* Form Request */}
      {canRequest && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Ajukan Perubahan Baru</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Alert Code</Label>
                <Select value={form.alert_code} onValueChange={(v) => setForm({ ...form, alert_code: v })}>
                  <SelectTrigger data-testid="thc-alert-code"><SelectValue placeholder="Pilih alert..." /></SelectTrigger>
                  <SelectContent>
                    {configs.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label || c.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Warning (opsional)</Label>
                <Input type="number" value={form.proposed_warning}
                        onChange={(e) => setForm({ ...form, proposed_warning: e.target.value })}
                        data-testid="thc-warning" />
              </div>
              <div>
                <Label className="text-xs">Critical (opsional)</Label>
                <Input type="number" value={form.proposed_critical}
                        onChange={(e) => setForm({ ...form, proposed_critical: e.target.value })}
                        data-testid="thc-critical" />
              </div>
              <div className="md:col-span-4">
                <Label className="text-xs">Alasan (minimal 5 karakter)</Label>
                <Textarea rows={2} value={form.reason}
                            onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            data-testid="thc-reason" />
              </div>
              <div className="md:col-span-4">
                <Button onClick={submit} data-testid="thc-submit">Ajukan Perubahan</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Riwayat Permintaan</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Alert</TableHead>
                <TableHead className="text-right">Current W/C</TableHead>
                <TableHead className="text-right">Proposed W/C</TableHead>
                <TableHead>Alasan</TableHead>
                <TableHead>Pemohon</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Diputuskan</TableHead>
                <TableHead>Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-xs text-slate-400 italic py-6">
                  Belum ada permintaan
                </TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`thc-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.alert_code}</TableCell>
                  <TableCell className="text-right text-xs">{r.current_warning ?? "—"} / {r.current_critical ?? "—"}</TableCell>
                  <TableCell className="text-right text-xs font-semibold">{r.proposed_warning ?? "—"} / {r.proposed_critical ?? "—"}</TableCell>
                  <TableCell className="text-xs max-w-xs" title={r.reason}>{r.reason}</TableCell>
                  <TableCell className="text-xs">{r.requested_by}<div className="text-[10px] text-slate-500">{r.requested_by_role}</div></TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_BADGE[r.status] || STATUS_BADGE.PENDING}
                             data-testid={`thc-status-${r.id}`}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.approved_at || r.rejected_at ? (
                      <>
                        <div>{new Date(r.approved_at || r.rejected_at).toLocaleString("id-ID")}</div>
                        <div className="text-[10px] text-slate-500">oleh {r.approved_by_role || "—"}</div>
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    {canApprove && r.status === "PENDING" ? (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => act(r.id, "approve")}
                                 data-testid={`thc-approve-${r.id}`}>
                          <Check size={11} className="mr-1" />Setujui
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => act(r.id, "reject")}
                                 data-testid={`thc-reject-${r.id}`}>
                          <X size={11} className="mr-1" />Tolak
                        </Button>
                      </div>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
